import { createSlice } from '@reduxjs/toolkit';

/**
 * Breadcrumbs are the only navigation state owned by Redux.
 *
 * Each entry is a self-contained snapshot of one stack item:
 *   - pathTemplate  — route template (e.g. /:cua/patients)
 *   - resolvedPath  — concrete URL (e.g. /abc/patients)
 *   - params        — params extracted from the URL (subset used by template)
 *   - isRoot        — whether this entry resets the stack on visit
 *
 * Labels are NOT stored in the entry. They are resolved lazily in
 * selectBreadcrumbsResolved so dynamic entity names stay reactive.
 *
 * The single mutator is replaceBreadcrumbs(stack) — the breadcrumbs sync
 * hook computes the next stack via pure functions and replaces the slice
 * state in one shot.
 *
 * The remaining action creators below are NO-OP stubs that exist purely
 * to keep the legacy navigateToRoute thunk compiling during the
 * migration window. They are deleted in the final cleanup task once all
 * callsites have moved off the thunk.
 */
const initialState = {
  breadcrumbs: [],
};

export const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    replaceBreadcrumbs: (state, action) => {
      state.breadcrumbs = action.payload;
    },
    // ----- legacy no-op stubs (removed in cleanup) -----
    setBreadcrumbs: () => {},
    updateBreadcrumbs: () => {},
    clearBreadcrumbs: () => {},
    initBreadcrumbsFromUrl: () => {},
    setNavigationError: () => {},
    clearNavigationError: () => {},
    setIsNavigating: () => {},
    handlePopState: () => {},
  },
});

export const {
  replaceBreadcrumbs,
  setBreadcrumbs,
  updateBreadcrumbs,
  clearBreadcrumbs,
  initBreadcrumbsFromUrl,
  setNavigationError,
  clearNavigationError,
  setIsNavigating,
  handlePopState,
} = navigationSlice.actions;

export default navigationSlice.reducer;
